import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { App, Switch } from 'antd'
import { useUploadStore } from '@/store'
import { useShallow } from 'zustand/react/shallow'
import type { MainPackageInfo, Platform, PlatformPackage, UploadTask } from '@/types'
import {
  isBackendHistoricalPackage,
  isPlatformPackageUploaded,
  platformLabel,
} from '../platform'
import { isTgzFile } from '../packageFile'
import errorIcon from '@/assets/upload-error-icon.svg'
import tickCircleIcon from '@/assets/tick-circle.png'
import RefreshIcon from '@/assets/reupload-refresh.svg?react'
import DocumentUploadIcon from '@/assets/document-upload.svg?react'
import { ActionButton } from './parts'

export default function PlatformBuildsSection({
  main_package,
  value,
  readOnly,
  isFirstVersion,
  historicalByPlatform,
  onChange,
  uploadContext,
}: {
  main_package: MainPackageInfo
  value: PlatformPackage[]
  readOnly?: boolean
  /** 首版须传齐全部声明平台，提示与开关行为不同 */
  isFirstVersion?: boolean
  /** 未勾选时可沿用的历史包（按平台） */
  historicalByPlatform?: Partial<Record<Platform, PlatformPackage>>
  onChange: (next: PlatformPackage[]) => void
  /** 平台包上传目标草稿 */
  uploadContext?: { app_id: string; version_id: string }
}) {
  const { t } = useTranslation()
  const { tasks, startPlatform, cancelTask, retryTask, removeTask } = useUploadStore(
    useShallow((s) => ({
      tasks: s.tasks,
      startPlatform: s.startPlatform,
      cancelTask: s.cancelTask,
      retryTask: s.retryTask,
      removeTask: s.removeTask,
    })),
  )

  const versionId = uploadContext?.version_id
  // 只认这一份草稿的子包任务，关掉抽屉再打开时进度还在
  const platformTasks = tasks.filter(
    (x) =>
      x.kind === 'platform' &&
      !!x.platform &&
      !!versionId &&
      x.version_id === versionId,
  )
  const slotOn = (p: Platform) =>
    isFirstVersion ||
    isPlatformPackageUploaded(value.find((pkg) => pkg.platform === p)) ||
    platformTasks.some((x) => x.platform === p)

  const [enabled, setEnabled] = useState<Partial<Record<Platform, boolean>>>(
    () =>
      Object.fromEntries(
        main_package.platforms.map((p) => [p, slotOn(p)]),
      ),
  )

  // 勾选上传新包会清掉 value 里的历史项；关掉开关时仍能还原
  const historicalCache = useRef<Partial<Record<Platform, PlatformPackage>>>(
    {},
  )
  useEffect(() => {
    for (const pkg of value) {
      if (isBackendHistoricalPackage(pkg)) {
        historicalCache.current[pkg.platform] = pkg
      }
    }
    for (const [p, pkg] of Object.entries(historicalByPlatform ?? {}) as Array<
      [Platform, PlatformPackage | undefined]
    >) {
      if (pkg && isBackendHistoricalPackage(pkg) && !historicalCache.current[p]) {
        historicalCache.current[p] = pkg
      }
    }
  }, [value, historicalByPlatform])

  // 详情晚到、或浮层里还有这份草稿的子包任务：把开关打开，进度才能画出来
  useEffect(() => {
    const openPlatforms = main_package.platforms.filter((p) => slotOn(p))
    if (openPlatforms.every((p) => enabled[p])) return
    setEnabled((s) => ({
      ...s,
      ...Object.fromEntries(openPlatforms.map((p) => [p, true])),
    }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, main_package.platforms, tasks, versionId])

  // 平台包上传成功 → 合并写入 value（避免同拍多成功互相覆盖）；再从浮层移除
  useEffect(() => {
    const succeeded = platformTasks.filter((x) => x.status === 'success')
    if (succeeded.length === 0) return

    let next = value
    let changed = false
    for (const task of succeeded) {
      const p = task.platform!
      const found = next.find((pkg) => pkg.platform === p)
      if (found && isPlatformPackageUploaded(found) && found.file_name === task.file_name) {
        continue
      }
      // 历史项的 package_url / from_version_id 属于旧包，不能带进新包
      const existing = found?.is_historical ? undefined : found
      changed = true
      next = [
        ...next.filter((pkg) => pkg.platform !== p),
        {
          // 保留详情刷新拿到的 upload_id / package_url，否则会被判成未上传
          ...existing,
          platform: p,
          file_name: task.file_name,
          size_mb: task.size_mb,
          package_version: main_package.version_code,
          is_historical: false,
          uploaded_locally: existing?.uploaded_locally,
        },
      ]
    }
    if (changed) onChange(next)
    succeeded.forEach((task) => removeTask(task.id))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tasks])

  const toggle = (p: Platform, on: boolean) => {
    // 首版不能关槽：必须传齐全部声明平台
    if (isFirstVersion && !on) return
    // 后端没有移除平台包的接口：已上传的平台不能关，只能重新上传
    if (!on && isPlatformPackageUploaded(value.find((pkg) => pkg.platform === p))) {
      return
    }
    setEnabled((s) => ({ ...s, [p]: on }))
    if (on) {
      // 历史包保留到新包上传成功再被覆盖，避免勾选后未上传就离开时丢失沿用
      onChange(
        value.filter(
          (pkg) => pkg.platform !== p || isBackendHistoricalPackage(pkg),
        ),
      )
      return
    }
    const historical =
      historicalCache.current[p] ?? historicalByPlatform?.[p]
    if (historical) {
      onChange([
        ...value.filter((pkg) => pkg.platform !== p),
        { ...historical, is_historical: true },
      ])
      return
    }
    onChange(value.filter((pkg) => pkg.platform !== p))
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div
        style={{
          padding: '0 14px',
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        <div style={{ fontSize: 14, fontWeight: 500, lineHeight: '22px', color: '#000' }}>
          {t(
            isFirstVersion
              ? 'versionForm.platformBuildsFirstHint'
              : 'versionForm.platformBuildsHint',
          )}
        </div>
        <div style={{ fontSize: 13, lineHeight: '20px', color: 'rgba(0,0,0,0.4)' }}>
          {t(
            isFirstVersion
              ? 'versionForm.platformBuildsFirstSubHint'
              : 'versionForm.platformBuildsSubHint',
          )}
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
        {main_package.platforms.map((p) => (
          <PlatformRow
            key={p}
            platform={p}
            enabled={isFirstVersion ? true : !!enabled[p]}
            readOnly={readOnly}
            lockToggle={isFirstVersion}
            pkg={value.find((x) => x.platform === p)}
            task={platformTasks.find((x) => x.platform === p)}
            onToggle={(on) => toggle(p, on)}
            onPick={(file) => {
              if (!uploadContext) return
              startPlatform(p, file, uploadContext)
            }}
            onCancel={(id) => cancelTask(id)}
            onRetry={(id) => retryTask(id)}
          />
        ))}
      </div>
    </div>
  )
}

function PlatformRow({
  platform,
  enabled,
  readOnly,
  lockToggle,
  pkg,
  task,
  onToggle,
  onPick,
  onCancel,
  onRetry,
}: {
  platform: Platform
  enabled: boolean
  readOnly?: boolean
  /** 首版：开关锁定为开启，不可关闭 */
  lockToggle?: boolean
  pkg?: PlatformPackage
  task?: UploadTask
  onToggle: (on: boolean) => void
  onPick: (file: File) => void
  onCancel: (id: string) => void
  onRetry: (id: string) => void
}) {
  const { t } = useTranslation()
  const { message } = App.useApp()
  const inputRef = useRef<HTMLInputElement>(null)
  const [dragOver, setDragOver] = useState(false)

  const uploading = task?.status === 'uploading'
  const parsing = task?.status === 'parsing'
  const failed = task?.status === 'failed' || task?.status === 'parse_failed'
  const uploaded =
    isPlatformPackageUploaded(pkg) && !uploading && !parsing && !failed
  const reusingHistorical = !!pkg && isBackendHistoricalPackage(pkg)
  const uploadedName =
    pkg?.file_name ||
    pkg?.package_url?.split('?')[0].split('/').pop() ||
    pkg?.upload_id ||
    ''

  const pick = (file?: File | null) => {
    if (!file || readOnly) return
    if (!isTgzFile(file)) {
      message.error(t('versionForm.unsupportedPackageFormat'))
      return
    }
    onPick(file)
  }

  const openPicker = () => inputRef.current?.click()

  const fileInput = (
    <input
      ref={inputRef}
      type="file"
      accept=".tgz"
      hidden
      onChange={(e) => {
        pick(e.target.files?.[0])
        e.target.value = ''
      }}
    />
  )

  let content: ReactNode = null

  if (enabled) {
    if (uploading && task) {
      content = (
        <GrayNest>
          <DashedInner padding="10px 12px">
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 10,
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 16,
                  width: '100%',
                  minWidth: 0,
                }}
              >
                <span
                  title={task.file_name}
                  style={{
                    fontSize: 14,
                    lineHeight: '22px',
                    minWidth: 0,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {task.file_name}
                  <span style={{ marginLeft: 10 }}>{task.size_mb} MB</span>
                </span>
                {!readOnly && (
                  <button
                    type="button"
                    onClick={() => onCancel(task.id)}
                    style={cancelLinkStyle}
                  >
                    {t('common.cancel')}
                  </button>
                )}
              </div>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 10,
                  width: '100%',
                }}
              >
                <div
                  style={{
                    flex: 1,
                    maxWidth: 420,
                    height: 6,
                    borderRadius: 8,
                    background: 'rgba(140, 140, 140, 0.16)',
                    overflow: 'hidden',
                  }}
                >
                  <div
                    style={{
                      width: `${task.progress}%`,
                      height: '100%',
                      borderRadius: 8,
                      background: 'var(--accent)',
                      transition: 'width 0.2s',
                    }}
                  />
                </div>
                <span
                  style={{
                    fontSize: 12,
                    fontWeight: 500,
                    lineHeight: '18px',
                    minWidth: 36,
                  }}
                >
                  {task.progress}%
                </span>
              </div>
            </div>
          </DashedInner>
        </GrayNest>
      )
    } else if (parsing && task) {
      content = (
        <GrayNest>
          <DashedInner padding="10px 12px">
            <div
              style={{
                textAlign: 'center',
                fontSize: 14,
                lineHeight: '22px',
                color: 'rgba(0,0,0,0.4)',
              }}
            >
              {t('versionForm.parsing')}
            </div>
          </DashedInner>
        </GrayNest>
      )
    } else if (failed && task) {
      content = (
        <GrayNest>
          <DashedInner padding="10px 12px">
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 10,
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 10,
                  fontSize: 14,
                  lineHeight: '22px',
                  minWidth: 0,
                  maxWidth: '100%',
                }}
              >
                <span
                  title={task.file_name}
                  style={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {task.file_name}
                </span>
                <span style={{ flexShrink: 0 }}>{task.size_mb} MB</span>
              </div>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                }}
              >
                <span
                  style={{
                    fontSize: 14,
                    lineHeight: '22px',
                    color: 'var(--color-status-danger)',
                  }}
                >
                  {task.status === 'parse_failed'
                    ? t('versionForm.parseFailed')
                    : t('versionForm.uploadFailed')}
                </span>
                <img src={errorIcon} alt="" width={24} height={24} />
              </div>
              {!readOnly && (
                <div style={{ display: 'flex', gap: 10, marginTop: 0 }}>
                  <ActionButton
                    icon={<RefreshIcon className="size-4" />}
                    onClick={() =>
                      task.status === 'failed' ? onRetry(task.id) : openPicker()
                    }
                  >
                    {t('versionForm.reupload')}
                  </ActionButton>
                  {task.status === 'failed' && (
                    <ActionButton
                      icon={<DocumentUploadIcon className="size-4" />}
                      onClick={openPicker}
                    >
                      {t('versionForm.selectAnother')}
                    </ActionButton>
                  )}
                </div>
              )}
            </div>
          </DashedInner>
        </GrayNest>
      )
    } else if (uploaded) {
      content = (
        <GrayNest>
          <div
            style={{
              border: '1px solid #00B42A',
              borderRadius: 14,
              height: 64,
              padding: '0 12px',
              background: 'var(--color-background-module-container)',
              display: 'flex',
              alignItems: 'center',
              boxSizing: 'border-box',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 16,
                width: '100%',
                padding: '0 20px',
                minWidth: 0,
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  flex: 1,
                  minWidth: 0,
                }}
              >
                <img
                  src={tickCircleIcon}
                  alt=""
                  width={22}
                  height={22}
                  style={{ flexShrink: 0, display: 'block' }}
                />
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 20,
                    minWidth: 0,
                    flex: 1,
                  }}
                >
                  <span
                    title={uploadedName}
                    style={{
                      fontSize: 14,
                      lineHeight: '22px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      minWidth: 0,
                    }}
                  >
                    {uploadedName}
                  </span>
                  {pkg!.size_mb != null && (
                    <span
                      style={{
                        fontSize: 14,
                        lineHeight: '22px',
                        color: 'rgba(0,0,0,0.4)',
                        flexShrink: 0,
                      }}
                    >
                      {pkg!.size_mb} MB
                    </span>
                  )}
                </div>
              </div>
              {!readOnly && (
                <ActionButton
                  icon={<RefreshIcon className="size-4" />}
                  onClick={openPicker}
                >
                  {t('versionForm.reupload')}
                </ActionButton>
              )}
            </div>
          </div>
        </GrayNest>
      )
    } else {
      content = (
        <div
          onDragOver={(e) => {
            e.preventDefault()
            setDragOver(true)
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            e.preventDefault()
            setDragOver(false)
            pick(e.dataTransfer.files?.[0])
          }}
          onClick={() => !readOnly && openPicker()}
          style={{
            ...grayNestStyle,
            cursor: readOnly ? 'default' : 'pointer',
          }}
        >
          <DashedInner padding="10px 12px" highlight={dragOver}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 13, lineHeight: '20px', color: '#000' }}>
                {t('versionForm.dropPlatformPackage')}
              </div>
              <div
                style={{
                  fontSize: 13,
                  lineHeight: '20px',
                  color: 'rgba(0,0,0,0.4)',
                  marginTop: 4,
                }}
              >
                {t('versionForm.platformPackageHint')}
              </div>
            </div>
          </DashedInner>
        </div>
      )
    }
  } else if (!reusingHistorical) {
    content = (
      <div style={{ fontSize: 13, color: 'rgba(0,0,0,0.4)', lineHeight: '20px' }}>
        {t('versionForm.noHistorical')}
      </div>
    )
  }

  return (
    <div
      style={{
        border: '1px solid var(--color-border-line)',
        borderRadius: 20,
        padding: 20,
        background: 'var(--color-background-module-container)',
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <Switch
          checked={enabled}
          disabled={
            readOnly ||
            lockToggle ||
            (enabled && isPlatformPackageUploaded(pkg))
          }
          onChange={onToggle}
          style={{ width: 45 }}
        />
        <span style={{ fontWeight: 500, fontSize: 14, lineHeight: '22px' }}>
          {platformLabel(platform)}
        </span>
        {!enabled && reusingHistorical && (
          <span style={{ fontSize: 13, color: 'rgba(0,0,0,0.4)', lineHeight: '20px' }}>
            {t('versionForm.reuseHistoricalInline', { version: pkg!.package_version })}
          </span>
        )}
      </div>

      {content}
      {fileInput}
    </div>
  )
}

/** 灰底外框：成功 / 上传中 / 失败 统一 padding 10 */
const grayNestStyle: CSSProperties = {
  border: '1px solid var(--color-border-line)',
  borderRadius: 20,
  padding: 10,
  background: 'var(--color-background-module-muted)',
}

function GrayNest({ children }: { children: ReactNode }) {
  return <div style={grayNestStyle}>{children}</div>
}

function DashedInner({
  children,
  padding,
  highlight,
}: {
  children: ReactNode
  padding: string
  highlight?: boolean
}) {
  return (
    <div
      style={{
        border: '1px dashed var(--color-brand-indigo)',
        borderRadius: 14,
        padding,
        background: highlight
          ? '#fff'
          : 'var(--color-background-module-container)',
        transition: 'background 0.15s',
      }}
    >
      {children}
    </div>
  )
}

const cancelLinkStyle: CSSProperties = {
  border: 'none',
  background: 'transparent',
  color: 'var(--color-brand-indigo)',
  fontSize: 14,
  fontWeight: 500,
  cursor: 'pointer',
  padding: 0,
  flexShrink: 0,
}
